/**
 * ===========================================================================
 *  synth.js — low level WebAudio synthesis primitives
 * ===========================================================================
 * Everything in Nusantara Warrior's soundtrack is built out of the handful of
 * generators below: noise buffers, envelope helpers, swept filters, inharmonic
 * partial banks and a glottal pulse wave. There are no sample files anywhere
 * in this project, so a "sword impact" is literally a noise transient plus a
 * detuned metal bank plus a pitch-dropping sine, mixed by envelope.
 *
 * NOTHING in here touches Math.random. Every generator takes an `rnd()`
 * closure that the audio system feeds from a fork of `world.rng`, so two runs
 * of the same capture synthesise bit-identical audio.
 * ===========================================================================
 */

export const FLOOR = 0.0001;          // exponentialRamp can never reach 0

/* ------------------------------------------------------------- buffers -- */

/**
 * White / pink / brown noise, 2 s by default. Sounds read a random offset into
 * it, so a 40 ms grass footstep taken twice never uses the same samples.
 */
export function makeNoiseBuffer(ctx, rnd, kind = 'white', seconds = 2.0) {
  const n = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buf = ctx.createBuffer(2, n, ctx.sampleRate);
  for (let c = 0; c < buf.numberOfChannels; c++) {
    const d = buf.getChannelData(c);
    if (kind === 'pink') {
      // Paul Kellet's economy pink filter — -3 dB/octave, the natural spectrum
      // of wind, distant crowds and cloth.
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
      for (let i = 0; i < n; i++) {
        const w = rnd() * 2 - 1;
        b0 = 0.99886 * b0 + w * 0.0555179; b1 = 0.99332 * b1 + w * 0.0750759;
        b2 = 0.96900 * b2 + w * 0.1538520; b3 = 0.86650 * b3 + w * 0.3104856;
        b4 = 0.55000 * b4 + w * 0.5329522; b5 = -0.7616 * b5 - w * 0.0168980;
        d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
        b6 = w * 0.115926;
      }
    } else if (kind === 'brown') {
      let last = 0;
      for (let i = 0; i < n; i++) { const w = rnd() * 2 - 1; last = (last + 0.02 * w) / 1.02; d[i] = last * 3.4; }
    } else {
      for (let i = 0; i < n; i++) d[i] = rnd() * 2 - 1;
    }
  }
  return buf;
}

/**
 * A convolution reverb impulse: exponentially decaying noise with a sparse
 * early-reflection pattern in front of it and progressive high-frequency
 * damping (a one-pole whose coefficient closes over the tail), which is what
 * makes it read as "a valley" instead of "a noise burst".
 */
export function makeImpulse(ctx, rnd, o = {}) {
  const seconds = o.seconds ?? 2.4;
  const decay = o.decay ?? 3.1;
  const predelay = o.predelay ?? 0.014;
  const damping = o.damping ?? 0.55;
  const sr = ctx.sampleRate;
  const n = Math.max(4, Math.floor(sr * seconds));
  const buf = ctx.createBuffer(2, n, sr);
  const pre = Math.floor(predelay * sr);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    let lp = 0;
    for (let i = pre; i < n; i++) {
      const u = (i - pre) / (n - pre);
      const a = Math.pow(1 - u, decay);
      // damping coefficient closes with time -> the tail goes dark
      const k = 1 - damping * (0.25 + 0.75 * u);
      lp += k * ((rnd() * 2 - 1) - lp);
      d[i] = lp * a;
    }
    // early reflections: 9 discrete taps in the first 90 ms, channel-decorrelated
    for (let r = 0; r < 9; r++) {
      const tt = pre + Math.floor((0.004 + rnd() * 0.086) * sr);
      if (tt < n) d[tt] += (rnd() * 2 - 1) * (0.55 - r * 0.05);
    }
  }
  return buf;
}

/** Soft-clip curve (tanh). `k` 1..40; 1 is nearly clean, 40 is a fuzz pedal. */
export function shaperCurve(k = 4, n = 1024) {
  const c = new Float32Array(n);
  const norm = Math.tanh(k);
  for (let i = 0; i < n; i++) { const x = (i / (n - 1)) * 2 - 1; c[i] = Math.tanh(k * x) / norm; }
  return c;
}

/**
 * Glottal-ish pulse wave: harmonics rolling off at 1/n^p with a slight even/odd
 * imbalance. Feeding this through formant bandpasses is what turns a buzz into
 * a human throat.
 */
export function pulseWave(ctx, harmonics = 28, p = 1.0, tilt = 0.12) {
  const real = new Float32Array(harmonics + 1);
  const imag = new Float32Array(harmonics + 1);
  for (let i = 1; i <= harmonics; i++) {
    imag[i] = (1 / Math.pow(i, p)) * (1 + (i % 2 === 0 ? -tilt : tilt));
  }
  return ctx.createPeriodicWave(real, imag, { disableNormalization: false });
}

/* ---------------------------------------------------------- envelopes --- */

/** Percussive AD envelope. Returns the time the tail reaches the floor. */
export function env(param, t, peak, atk, dec, floor = FLOOR) {
  const p = Math.max(floor * 2, peak);
  param.setValueAtTime(floor, t);
  if (atk > 0.0005) param.linearRampToValueAtTime(p, t + atk); else param.setValueAtTime(p, t + atk);
  param.exponentialRampToValueAtTime(floor, t + atk + Math.max(0.004, dec));
  return t + atk + dec;
}

/** ASR envelope for sustained material (pads, horns, held vocals). */
export function envASR(param, t, peak, atk, hold, rel, floor = FLOOR) {
  const p = Math.max(floor * 2, peak);
  param.setValueAtTime(floor, t);
  param.linearRampToValueAtTime(p, t + atk);
  param.setValueAtTime(p, t + atk + hold);
  param.exponentialRampToValueAtTime(floor, t + atk + hold + Math.max(0.01, rel));
  return t + atk + hold + rel;
}

/** Exponential parameter glide, safe for zero-crossing values. */
export function sweep(param, t, a, b, dur) {
  const A = Math.max(FLOOR, a), B = Math.max(FLOOR, b);
  param.setValueAtTime(A, t);
  param.exponentialRampToValueAtTime(B, t + Math.max(0.004, dur));
}

/* -------------------------------------------------------------- nodes --- */

export function gainNode(ctx, v = 1) { const g = ctx.createGain(); g.gain.value = v; return g; }

export function filter(ctx, type, freq, q = 1) {
  const f = ctx.createBiquadFilter();
  f.type = type; f.frequency.value = freq; f.Q.value = q;
  return f;
}

/** A one-shot slice of a noise buffer, started at `t`, from a random offset. */
export function noiseShot(ctx, buf, t, dur, rnd, rate = 1) {
  const s = ctx.createBufferSource();
  s.buffer = buf;
  s.playbackRate.value = rate;
  const off = rnd() * Math.max(0.001, buf.duration - dur * rate - 0.01);
  s.start(t, off, dur * rate + 0.01);
  return s;
}

export function osc(ctx, type, freq, t, detune = 0) {
  const o = ctx.createOscillator();
  if (type === 'periodic') { /* caller sets the wave */ } else o.type = type;
  o.frequency.setValueAtTime(freq, t);
  if (detune) o.detune.setValueAtTime(detune, t);
  return o;
}

/**
 * A bank of inharmonic partials — the core of every metal sound in the game.
 * Square waves at irrational ratios through a bandpass give the clangorous,
 * slightly dissonant ring of a struck blade; the ratios are jittered per shot
 * so no two clangs are the same bell.
 */
export function metalBank(ctx, out, t, o, rnd) {
  const f0 = o.f0 ?? 1300;
  const ratios = o.ratios || [1, 1.427, 1.813, 2.372, 3.140, 4.108];
  const dur = o.dur ?? 0.5;
  const type = o.type || 'square';
  const jitter = o.jitter ?? 0.06;
  const bp = filter(ctx, 'bandpass', o.bp ?? f0 * 1.9, o.q ?? 0.9);
  const hp = filter(ctx, 'highpass', o.hp ?? 500, 0.7);
  const g = gainNode(ctx, 0);
  bp.connect(hp); hp.connect(g); g.connect(out);
  env(g.gain, t, o.gain ?? 0.4, o.atk ?? 0.0015, dur);
  if (o.bpTo) sweep(bp.frequency, t, o.bp ?? f0 * 1.9, o.bpTo, dur * 0.8);
  const n = Math.min(ratios.length, o.n ?? ratios.length);
  for (let i = 0; i < n; i++) {
    const f = f0 * ratios[i] * (1 + (rnd() * 2 - 1) * jitter);
    const o1 = ctx.createOscillator();
    o1.type = type; o1.frequency.setValueAtTime(f, t);
    if (o.bend) o1.frequency.exponentialRampToValueAtTime(Math.max(20, f * o.bend), t + dur);
    const gi = gainNode(ctx, 0);
    // higher partials die faster — physical, and it stops the tail turning into a whistle
    const life = dur * (1 - i * 0.11) * (0.8 + rnd() * 0.4);
    env(gi.gain, t, (1 / (1 + i * 0.75)), 0.001, Math.max(0.02, life));
    o1.connect(gi); gi.connect(bp);
    o1.start(t); o1.stop(t + dur + 0.05);
  }
  return g;
}

/** Pitch-dropping sine — the "body" under every impact. */
export function bodyThump(ctx, out, t, o, rnd) {
  const f0 = (o.f0 ?? 170) * (1 + (rnd() * 2 - 1) * (o.jitter ?? 0.08));
  const f1 = (o.f1 ?? 46) * (1 + (rnd() * 2 - 1) * (o.jitter ?? 0.08));
  const dur = o.dur ?? 0.22;
  const o1 = ctx.createOscillator();
  o1.type = o.type || 'sine';
  sweep(o1.frequency, t, f0, f1, dur * (o.pitchDur ?? 0.55));
  const g = gainNode(ctx, 0);
  env(g.gain, t, o.gain ?? 0.7, o.atk ?? 0.002, dur);
  o1.connect(g); g.connect(out);
  o1.start(t); o1.stop(t + dur + 0.05);
  return g;
}

/** Band-limited noise burst with a swept centre — whooshes, cloth, dirt, fire. */
export function noiseBand(ctx, out, t, buf, o, rnd) {
  const dur = o.dur ?? 0.25;
  const bp = filter(ctx, o.type || 'bandpass', o.f0 ?? 900, o.q ?? 1.4);
  if (o.f1 !== undefined) {
    const mid = o.fMid ?? null;
    if (mid !== null) {
      sweep(bp.frequency, t, o.f0, mid, dur * 0.4);
      bp.frequency.exponentialRampToValueAtTime(Math.max(20, o.f1), t + dur);
    } else sweep(bp.frequency, t, o.f0, o.f1, dur);
  }
  const g = gainNode(ctx, 0);
  if (o.hold) envASR(g.gain, t, o.gain ?? 0.3, o.atk ?? 0.01, o.hold, o.dec ?? dur);
  else env(g.gain, t, o.gain ?? 0.3, o.atk ?? 0.01, o.dec ?? dur);
  const s = noiseShot(ctx, buf, t, dur + (o.hold || 0) + 0.06, rnd, o.rate ?? 1);
  s.connect(bp); bp.connect(g); g.connect(out);
  s.stop(t + dur + (o.hold || 0) + (o.dec ?? dur) + 0.06);
  return g;
}

/** Very short click/tick: 1-4 ms of highpassed noise. The "attack" of anything. */
export function transient(ctx, out, t, buf, o, rnd) {
  const dur = o.dur ?? 0.006;
  const hp = filter(ctx, o.type || 'highpass', o.f0 ?? 2200, o.q ?? 0.8);
  const g = gainNode(ctx, 0);
  env(g.gain, t, o.gain ?? 0.5, 0.0006, dur);
  const s = noiseShot(ctx, buf, t, dur + 0.02, rnd);
  s.connect(hp); hp.connect(g); g.connect(out);
  s.stop(t + dur + 0.03);
  return g;
}

/** Semitones -> ratio. */
export const st2r = (s) => Math.pow(2, s / 12);
/** MIDI-ish: semitones above A2 (110 Hz). */
export const noteHz = (semi, base = 110) => base * Math.pow(2, semi / 12);
